import { Icon, OspToast, ospThemes } from "@ospweb/sdk-ui";
import { ChatCategoryCardProps, MediaType } from "./type";
import {
  PropertiesType,
  getPropertiesAddress,
  isFetchEnable,
  useSwapToken,
} from "@/src/hooks/useSwapToken";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn, copyText, ellipsisMiddle, pubsub } from "utils";
import {
  transformChatCardItemToCardItem,
  useIsPc,
  useTokenPopoverData,
} from "@hooks";

import { ActionButtons } from "../TokenPopover/ActionButtons";
import { I18nUtil } from "@utils/i18n";
import { ShellLink } from "@/src/components";
import NextImage from "../../NextImage";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import PriceDisplay from "../../PriceDisplay";
import { PriceFormatResult } from "../../PriceDisplay/types";
import { ROUTER_PATH } from "@/src/config";
import { SwapSingleModal } from "../../swap";
import { TokenPopover } from "../TokenPopover";
import Translations from "@translate/components/Chat/Cards/ChatCategoryCard";
import { formatBigNumber } from "@/src/utils";
import log from "logbeacon";
import { motion } from "motion/react";
import { pubSubKeyMap } from "@/src/utils/pubSubKeyMap";
import { transformCardsToXPayData } from "./utils/xPayDataTransform";
import { useMemoizedFn } from "ahooks";

const I18n = I18nUtil.createScoped(Translations);

/**
|--------------------------------------------------
| 搜索的头部卡片，包含：项目、人、币、生态
|--------------------------------------------------
*/
const ChatCategoryCardOrg: React.FC<ChatCategoryCardProps> = ({
  cardItem,
  isSwap = true,
  enabled = false,
  disabled,
}) => {
  const isHKStock = cardItem?.properties.currency !== "USD";
  const isCtypto = cardItem?.type === "token" || cardItem?.type === "pool";
  const isStock = cardItem?.type === "stock";
  // const router = useShellRouter();
  const [isPC] = useIsPc();
  const [btnName, setBtnName] = useState("copy-action");
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isSttockOrCrypto = isCtypto || isStock;
  // 快照价格
  const xPayData = useMemo(() => {
    if (!cardItem) {
      log.debug("[Message] 不显示XPayReportCard");
      return undefined;
    }

    return transformCardsToXPayData(cardItem);
  }, [cardItem]);

  const address = useMemo(() => {
    if (cardItem?.type === "token") {
      return cardItem.properties?.addresses?.[0].address;
    }
    if (cardItem?.type === "pool") {
      return cardItem.properties?.base_address;
    }
    return "";
  }, [cardItem]);
  // TokenPopover 数据处理逻辑 - 使用新的 useTokenPopoverData hook
  const convertedCardItem = transformChatCardItemToCardItem(cardItem);
  const {
    data: tokenPopoverData,
    shouldShow: shouldShowTokenPopover,
    invalidateCache,
  } = useTokenPopoverData(convertedCardItem);

  const handleViewReport = () => {
    if (!enabled) {
      OspToast.info({
        content: I18n.t("Please retry after the current response completes"),
      });
      return;
    }
    pubsub.emit(pubSubKeyMap.CHAT_360, {
      id: tokenPopoverData?.token?.id || "",
      type: tokenPopoverData?.token?.type === "token" ? "crypto" : "stock",
      symbol: tokenPopoverData?.token.symbol || "",
    });
  };

  const showSocial =
    cardItem?.type !== "token" &&
    cardItem?.type !== "stock" &&
    cardItem?.type !== "pool";
  const handleCopyAddress = useCallback(async (address: string) => {
    if (!address) {
      return;
    }
    try {
      await copyText(address);
      setBtnName("check");
      // 清理之前的定时器
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      // 保存新的定时器引用
      timerRef.current = setTimeout(() => {
        setBtnName("copy-action");
      }, 3000);
    } catch (error) {
      log.error("[文案]Failed to copy:", error);
    }
  }, []);

  const swapTokenParams = getPropertiesAddress(
    cardItem?.properties as PropertiesType,
  );
  const querySymbol =
    cardItem?.properties?.ticker || cardItem?.properties?.token_symbol;
  const { enable: fetchEnable } = isFetchEnable(swapTokenParams);
  const { data, isLoading } = useSwapToken(swapTokenParams);
  const goSwap = useMemoizedFn(() => {
    SwapSingleModal.setVisible(true, data);
  });

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // 渲染函数添加 memo 优化
  const renderTags = useMemo(() => {
    const TagList = (tags: string[] = []) => {
      if (!tags?.length) {
        return null;
      }
      return (
        <div className="flex flex-row gap-1 flex-wrap">
          {tags.map((tag, index) => (
            <div
              key={`${tag}-${index}`}
              className="bg-background_white_overlay10 rounded-1 px-[6px] text-content_secondary py-[2px] items-center flex-wrap body_medium_s"
            >
              {tag}
            </div>
          ))}
        </div>
      );
    };

    TagList.displayName = "TagList";
    return TagList;
  }, []);

  const renderSocials = useMemo(() => {
    const SocialLinks = (socials: MediaType[] = []) => {
      if (!socials?.length) {
        return null;
      }
      return (
        <div className="flex flex-row items-center gap-3 max-md:gap-1 mt-3 max-md:mt-0">
          {socials.map((media, index) => (
            <ShellLink
              aria-label="Tag List"
              data-openreplay-label={`Tag List Link | ${media.link}`}
              id="lnk-tag-list"
              href={media.link}
              key={`${media.link}-${index}`}
              target="_blank"
              className="flex justify-center items-center rounded-[8px] hover:cursor-pointer object-cover hover:bg-background_white_overlay5 hover:backdrop-blur-[16px]"
            >
              <NextImage
                width={20}
                height={20}
                src={media.icon}
                alt={`social-media-${index}`}
              />
            </ShellLink>
          ))}
        </div>
      );
    };

    SocialLinks.displayName = "SocialLinks";
    return SocialLinks;
  }, []);

  const cardLink = useMemo(() => {
    const linkMap = {
      token: `${ROUTER_PATH.CRYPTO_TOKEN}/${cardItem?.properties?.id}`,
      pool: `${ROUTER_PATH.POOL_TOKEN}/${cardItem?.properties?.id}`,
      stock: `${ROUTER_PATH.STOCK_DETAIL}/${cardItem?.properties?.symbol}`,
    };

    return linkMap[cardItem?.type as keyof typeof linkMap] || "";
  }, [cardItem]);

  // 生成 forecast URL，逻辑与 xPayDataTransform.ts 类似
  const forecastUrl = useMemo(() => {
    const type = cardItem?.type;
    let tokenIdentifier = "";

    if (type === "pool" || type === "token") {
      tokenIdentifier = cardItem?.properties?.id || "";
    } else if (type === "stock") {
      tokenIdentifier = cardItem?.properties?.symbol || "";
    }

    if (type === "stock" && isHKStock) {
      return `/${type}/${tokenIdentifier}`;
    }

    return type && tokenIdentifier
      ? `/${type === "token" ? "crypto" : type}/${tokenIdentifier}/forecast`
      : undefined;
  }, [cardItem, isHKStock]);

  // forecast 点击处理函数
  const onForecastClick = useCallback(() => {
    if (forecastUrl) {
      // router.push(forecastUrl);
      window.open(forecastUrl, "_blank");
    }
  }, [forecastUrl]);

  const Tag = cardLink ? ShellLink : "div";

  const tokenPrice = xPayData?.price.current as PriceFormatResult;

  const PriceContent = (
    <div
      className={cn("flex gap-1 mt-1 items-end", {
        ["max-md:my-4"]: isSttockOrCrypto,
      })}
    >
      <PriceDisplay
        priceData={tokenPrice}
        text={
          !isCtypto
            ? formatBigNumber(xPayData?.price?.current as string, {
                needFormat: true,
                trimZeros: true,
                precision: 2,
              })
            : undefined
        }
        classNames={{
          container: isCtypto
            ? "headline_regular_m text-content_primary"
            : "headline_regular_m text-content_primary",
          zeroCount: "body_medium_s",
        }}
        hideUnitSymbol={false}
        currencySymbol={xPayData?.price.currency}
      />
      {isSttockOrCrypto && (
        <div className="flex gap-0.5 items-center body_bold_m">
          {xPayData?.price.change.status !== "flat" && (
            <>
              <OspIcons
                name={
                  xPayData?.price.change.status === "up"
                    ? "upvote"
                    : xPayData?.price.change.status === "down"
                      ? "downvote"
                      : "minus"
                }
                size={12}
                className={
                  xPayData?.price.change.status === "up"
                    ? "text-content_accent1_primary"
                    : xPayData?.price.change.status === "down"
                      ? "text-content_accent2_primary"
                      : "text-content_tertiary"
                }
              />
              <div
                className={
                  xPayData?.price.change.status === "up"
                    ? "text-content_accent1_primary"
                    : xPayData?.price.change.status === "down"
                      ? "text-content_accent2_primary"
                      : "text-content_tertiary"
                }
              >
                {xPayData?.price.change.percentage}
              </div>
            </>
          )}
        </div>
      )}
      {xPayData?.price.change.status !== "flat" && isCtypto && (
        <div className="body_m text-content_tertiary">
          {xPayData?.price.label}
        </div>
      )}
    </div>
  );

  const onVisibilityChange = (visable: boolean) => {
    if (!visable) {
      invalidateCache();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.8 } }}
      className="overflow-auto min-h-16"
    >
      <div className="flex flex-col">
        <div className="flex flex-row w-full items-center gap-3 justify-between">
          <TokenPopover
            disabled={!isPC || !shouldShowTokenPopover || disabled}
            data={tokenPopoverData}
            onForecastClick={onForecastClick}
            handleViewReport={handleViewReport}
            onVisibilityChange={onVisibilityChange}
          >
            <div className="flex items-center">
              <div className="flex-row flex-center mr-2">
                <div className="w-11 h-11 md:w-14 md:h-14 rounded-full overflow-hidden">
                  <NextImage
                    className="w-full h-full object-cover rounded-full"
                    src={
                      cardItem?.properties?.icon || cardItem?.properties?.logo
                    }
                    alt="avatar"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <div>
                  <Tag
                    href={cardLink || ""}
                    target="_blank"
                    data-openreplay-label={
                      cardLink
                        ? `ChatCategoryCard · Title Link | ${cardLink}`
                        : undefined
                    }
                    className={cn(
                      "gap-1 cursor-pointer flex flex-row items-baseline text-content_primary hover:text-content_primary items-center",
                      {
                        ["body_medium_m"]:
                          cardItem?.type === "token" ||
                          cardItem?.type === "pool" ||
                          cardItem?.type === "stock",
                        ["headline_regular_s"]:
                          cardItem?.type === "project" ||
                          cardItem?.type === "ecosystem" ||
                          cardItem?.type === "person",
                      },
                    )}
                  >
                    {cardItem?.properties?.name}
                    {cardItem?.properties?.one_liner &&
                      cardItem.type === "person" && (
                        <div className="text-content_tertiary body_m line-clamp-2 ml-0.5">
                          {cardItem?.properties?.one_liner}
                        </div>
                      )}
                    {(cardItem?.properties?.ticker ||
                      cardItem?.properties?.short_name ||
                      cardItem?.properties?.company_name) && (
                      <span className="text-content_tertiary body_m flex-center">
                        {cardItem?.properties?.ticker ||
                          cardItem?.properties?.short_name ||
                          cardItem?.properties?.company_name}
                      </span>
                    )}
                  </Tag>
                  {isPC && isSttockOrCrypto && PriceContent}
                  {!isPC && (
                    <div className="text-content_tertiary flex flex-row gap-1 body_m items-center">
                      {address && (
                        <>
                          <div>{ellipsisMiddle(address)}</div>
                          <Icon
                            data-openreplay-label={btnName}
                            name={btnName}
                            size={16}
                            onClick={() => handleCopyAddress(address || "")}
                            color={`${btnName === "copy-action" ? ospThemes.colors.content_tertiary : ospThemes.colors.content_accent1_primary}`}
                            className="hover:cursor-pointer"
                          />
                        </>
                      )}
                      {renderSocials(cardItem?.properties?.social_medias || [])}
                    </div>
                  )}
                </div>
                {isPC && renderTags(cardItem?.properties?.tags || [])}
              </div>
            </div>
          </TokenPopover>
          <div className="flex flex-col  gap-2 items-end">
            {isPC &&
              showSocial &&
              renderSocials(cardItem?.properties?.social_medias || [])}
            <div>
              <div className="flex gap-3 items-center">
                {forecastUrl && (
                  <ShellLink
                    aria-label="Tag List"
                    data-openreplay-label={`Tag List Link | ${forecastUrl}`}
                    id="lnk-tag-list-2"
                    href={forecastUrl}
                    target="_blank"
                    className={cn(
                      "button_m py-2 px-3 border-border_white_transparent20 text-content_primary border w-fit h-fit rounded-2",
                      "max-md:py-[6px] max-md:px-[8px]",
                      "inline-block text-center no-underline",
                      "hover:bg-background_white_overlay5",
                    )}
                  >
                    {!isHKStock
                      ? I18n.t("%{var0} Forecast", {
                          var0:
                            cardItem?.properties?.ticker ||
                            cardItem?.properties?.token_symbol ||
                            cardItem?.properties?.symbol,
                        })
                      : I18n.t("View Detail")}
                  </ShellLink>
                )}
                {((!!fetchEnable && !!data?.address) ||
                  (!fetchEnable && !!querySymbol)) &&
                  isSwap &&
                  (isPC ? (
                    <button
                      type="button"
                      data-openreplay-label={`ChatCategoryCard · Button | ${I18n.t("Swap")}`}
                      className={cn(
                        "button sm_button tonal_button button_m group self-end px-3",
                      )}
                      data-loading={isLoading}
                      disabled={isLoading}
                      onClick={goSwap}
                    >
                      <span className="flex items-center group-loading:hidden">
                        <OspIcons
                          name="transfer-alt"
                          size={20}
                          className="mr-1"
                          color={ospThemes.colors.content_primary}
                        />
                        {I18n.t("Swap")}
                      </span>
                      <Icon
                        name="ic_loading2"
                        size={16}
                        color={ospThemes.colors.content_primary}
                        className={cn(
                          "hidden animate-spin group-loading:block",
                        )}
                      />
                    </button>
                  ) : (
                    <div className="p-3 bg-background_white_overlay10 flex  items-center justify-center rounded-2">
                      <OspIcons
                        name="transfer-alt"
                        size={20}
                        color={ospThemes.colors.content_primary}
                      />
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
        {cardItem?.properties?.one_liner && cardItem.type === "ecosystem" && (
          <div className="text-content_tertiary body_m line-clamp-2 my-4 md:my-3">
            {cardItem?.properties?.one_liner}
          </div>
        )}
        {!isPC && renderTags(cardItem?.properties?.tags || [])}
        {!isPC && isSttockOrCrypto && (
          <>
            {PriceContent}
            <ActionButtons
              isHKStock={isHKStock}
              symbol={tokenPopoverData?.token.symbol || ""}
              showReport={!!tokenPopoverData?.score} // 根据score字段存在与否控制显示
              onForecastClick={onForecastClick}
              onReportClick={handleViewReport}
            />
          </>
        )}
      </div>
    </motion.div>
  );
};

const ChatCategoryCard = memo(ChatCategoryCardOrg);
export default ChatCategoryCard;
